<template>
  <div>
    <Vuedraggable
      :list="groupedList"
      class="tour-day-item-container d-flex flex-wrap align-items-center"
      draggable=".item-container:not(.not-draggable)"
      item-key="id"
      v-bind="dragOptions"
      @change="onChanged"
    >
      <template #item="{ element }">
        <div
          v-if="!!element.hotel"
          :class="{
            'not-draggable ghost-item': element.disabled || props.updatingTour,
            'not-draggable blocked-item': element.isBlocked,
            'item-selected': showHotelDropdown,
          }"
          class="item-container position-relative mb-12 me-16"
          :style="showHotelDropdown ? 'z-index: 9999 !important;' : ''"
        >
          <!-- Actions on Top -->
          <div class="tour-day-item-actions d-flex justify-content-center mb-4">
            <v-button
              :disabled="props.updatingTour"
              class="p-1 dropdown-toggle bg-white rounded-circle shadow-sm border"
              size="xs"
              variant="icon"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              @click="toggleHotelList(!showHotelDropdown)"
            >
              <v-icon icon="group" size="xs" class="text-primary" title="Click to see all suppliers" />
            </v-button>
            <ul class="dropdown-menu pt-0 custom-scroll shadow-lg border-0 rounded-xs">
              <li class="dropdown-header p-8 d-flex justify-content-between align-items-center border-bottom bg-light">
                <div class="d-flex align-items-center gap-2">
                  <span class="text-xs fw-bold">{{ t("tourBuilder.nights") }}:</span>
                  <div class="d-flex align-items-center bg-white rounded-pill px-4 border shadow-sm">
                    <v-button 
                      class="p-0 text-primary" 
                      size="xs" 
                      variant="icon"
                      @click="decreaseNights"
                      :disabled="getMinNights() <= 1"
                    >
                      <v-icon icon="remove" size="xs" />
                    </v-button>
                    <div class="mx-8 text-xs fw-bold">{{ getTotalNights() }}</div>
                    <v-button 
                      class="p-0 text-primary" 
                      size="xs" 
                      variant="icon"
                      @click="increaseNights"
                      :disabled="getTotalNights() >= getMaxNights()"
                    >
                      <v-icon icon="plus" size="xs" />
                    </v-button>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-4">
                  <v-button 
                    class="btn-icon p-1" 
                    size="xs" 
                    variant="outline-danger" 
                    @click="deleteAllHotels"
                    :disabled="props.updatingTour || !props.list?.some((item: any) => item.type === 'hotel' && !item.isAutoGenerated)"
                  >
                    <v-icon icon="trash" size="xs" />
                  </v-button>
                  <v-button class="btn-icon p-1" size="xs" variant="primary" @click="addHotel">
                    <v-icon icon="plus" size="xs" />
                  </v-button>
                </div>
              </li>
              <Vuedraggable
                :list="element.hotel"
                class="hotel-list"
                draggable=".hotel-row"
                item-key="id"
                v-bind="hotelDragOptions"
                @change="onHotelChanged"
                              >
                  <template #item="{ element: hotel }">
                    <li
                    :key="hotel.id"
                    class="d-flex justify-content-between align-items-center border-bottom py-2 px-8 dropdown-item hotel-row"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <v-icon icon="group" size="xs" class="opacity-50" />
                      <div class="text-xs text-truncate" style="max-width: 120px;">
                        {{ hotel.supplier ? hotel.supplier : t("tourBuilder.selectHotel") }}
                      </div>
                    </div>

                    <div class="d-flex align-items-center gap-2 hotel-actions">
                      <v-button
                        :disabled="props.updatingTour || hotel.isAutoGenerated"
                        class="p-1 bg-white border rounded-circle shadow-sm"
                        size="xs"
                        variant="icon"
                        @click="
                          setCurrentItem(hotel);
                          toggleHotelList(false);
                        "
                      >
                        <v-icon icon="pencil" size="xs" class="text-primary" />
                      </v-button>

                      <v-button
                        :disabled="props.updatingTour || hotel.isAutoGenerated"
                        class="p-1 bg-white border rounded-circle shadow-sm"
                        size="xs"
                        variant="icon"
                        @click="
                          deleteItem(hotel, hotel.supplier);
                          toggleHotelList(false);
                        "
                      >
                        <v-icon icon="trash" size="xs" class="text-danger" />
                      </v-button>
                    </div>
                  </li>
                </template>
              </Vuedraggable>
            </ul>
          </div>

          <!-- Icon in Middle (Square Shape) -->
          <div class="d-flex justify-content-center">
            <div
              :class="{
                'not-draggable ghost-hotel-item': element.disabled,
                'blocked-item': element.isBlocked,
              }"
              class="tour-day-item d-flex align-items-center justify-content-center position-relative bg-white border rounded shadow-sm"
              style="width: 3.5rem; height: 3.5rem;"
            >
              <span
                class="position-absolute top-0 end-0 bg-primary text-white rounded-circle fw-bold shadow-sm d-flex align-items-center justify-content-center"
                style="width: 18px; height: 18px; transform: translate(30%, -30%); z-index: 1; font-size: 9px; border: 1.5px solid #fff;"
              >
                {{ element.hotel.length }}
              </span>
              <v-icon
                :icon="resolveItemIconKey(element)"
                class="text-xxl service-icon"
                :style="{ color: getServiceIconColor(resolveItemTypeKey(element)) }"
              />
            </div>
          </div>

          <!-- Name on Bottom -->
          <div
            class="supplier-name text-xs text-center fw-medium text-dark mt-2 mx-auto"
            :title="element.supplier"
            style="width: 4.25rem; word-break: break-word; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;"
          >
            {{ element.supplier }}
          </div>
        </div>

        <div
          v-else
          :class="{
            'not-draggable ghost-item': element.disabled || props.updatingTour,
            'not-draggable blocked-item': element.isBlocked,
            'item-selected': element.id === userStore.currentTourDayItem?.id,
          }"
          class="item-container position-relative mb-12 me-16"
        >
          <!-- Actions on Top -->
          <div class="tour-day-item-actions d-flex justify-content-center mb-4">
            <v-button
              :disabled="props.updatingTour || element.isAutoGenerated"
              class="p-1 bg-white border rounded-circle shadow-sm me-4"
              size="xs"
              variant="icon"
              @click="setCurrentItem(element)"
            >
              <v-icon icon="pencil" size="xs" class="text-primary" />
            </v-button>
            <v-button
              v-if="isLuggageItem(element)"
              :disabled="props.updatingTour || element.isAutoGenerated"
              class="p-1 bg-white border rounded-circle shadow-sm me-4"
              size="xs"
              variant="icon"
              :title="$t('tourBuilder.copyLuggageToDays')"
              @click="openCopyLuggageModal(element)"
            >
              <span class="mi mi-copy" style="font-size: 12px; line-height: 1;" />
            </v-button>

            <v-button
              :disabled="props.updatingTour || element.isAutoGenerated"
              class="p-1 bg-white border rounded-circle shadow-sm"
              size="xs"
              variant="icon"
              @click="deleteItem(element, element.supplier)"
            >
              <v-icon icon="trash" size="xs" class="text-danger" />
            </v-button>
          </div>

          <!-- Icon in Middle (Square Shape) -->
          <div class="d-flex justify-content-center">
            <div
              :class="{
                'not-draggable ghost-hotel-item': element.disabled,
                'blocked-item': element.isBlocked,
              }"
              class="tour-day-item d-flex align-items-center justify-content-center position-relative bg-white border rounded shadow-sm"
              style="width: 3.5rem; height: 3.5rem;"
            >
              <v-icon
                :icon="resolveItemIconKey(element)"
                class="text-xxl service-icon"
                :style="{ color: getServiceIconColor(resolveItemTypeKey(element)) }"
              />
            </div>
          </div>

          <!-- Name on Bottom -->
          <div
            class="supplier-name text-xs text-center fw-medium text-dark mt-2 mx-auto"
            :title="element.supplier"
            style="width: 4.25rem; word-break: break-word; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;"
          >
            <template v-if="element.type === 'stage_information'">
              {{ element?.model?.title ?? '' }}
            </template>
            <template v-else-if="element.type === 'general_information'">
              {{ element?.model?.title ?? '' }}
            </template>
            <template v-else>
              {{ element.supplier }}
            </template>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="ms-8 d-flex align-items-center align-self-center" style="height: 3.5rem;">
          <v-button
            :disabled="props.updatingTour || !props.canAddItem"
            class="btn-add-plus rounded-circle shadow-sm d-flex align-items-center justify-content-center border-0 p-0"
            size="xs"
            variant="primary"
            @click="openModal('the-template-day-item-create-modal')"
          >
            <v-icon icon="plus" class="m-0" style="font-size: 10px; line-height: 1;" />
          </v-button>
        </div>
      </template>
    </Vuedraggable>

    <the-template-day-item-create-modal-v2
      v-if="modalToShow === 'the-template-day-item-create-modal'"
      :agency="props.agency"
      :template-id="props.templateId"
      :tour-day-id="props.templateDayId"
      @closed="closeModal"
      @emit-reload-tour="emitReloadTour"
      @open-details="setCurrentItem"
    />

    <TheTemplateCopyLuggageToDaysModalV2
      v-if="copyLuggageModalOpen && userStore.currentTourDayItem"
      :agency="props.agency"
      :template-id="props.templateId"
      :source-tour-day-item="(userStore.currentTourDayItem as Record<string, unknown>)"
      @closed="copyLuggageModalOpen = false"
      @applied="
        copyLuggageModalOpen = false;
        emit('emitReloadTour');
      "
    />
  </div>
</template>

<script lang="ts" setup>
import { getCurrentInstance, PropType, ref } from "vue";
import Vuedraggable from "vuedraggable";
import { TYPE, useToast } from "vue-toastification";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import useModalUtils from "@/helpers/ModalUtils";
import { useUserStore } from "@/stores/user";
import { findConceptByKey, mapConcepts } from "@/helpers/ConceptHelper";
import useConcepts from "@/helpers/Concepts";
import TheTemplateDayItemCreateModalV2 from "@/components/template/V2/TheTemplateDayItemCreateModalV2.vue";
import TheTemplateCopyLuggageToDaysModalV2 from "@/components/template/V2/TheTemplateCopyLuggageToDaysModalV2.vue";
import api from "@/api";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { getServiceIconColor } from "@/helpers/ServiceIconColor";

const props = defineProps({
  list: {
    type: Array as PropType<Record<string, unknown>[]>,
    required: false,
  },
  templateDayId: {
    type: Number,
    required: true,
  },
  type: {
    type: String,
    required: false,
    default: "",
  },
  templateId: {
    type: Number,
    required: true,
  },
  agency: {
    type: String,
    required: true,
  },
  updatingTour: {
    type: Boolean,
    required: false,
    default: false,
  },
  canAddItem: {
    type: Boolean,
    required: false,
    default: true,
  },
  tourMapped: {
    type: Object,
    required: true,
  },
  originId: {
    type: Number,
    required: false,
    default: null,
  },
  destinationId: {
    type: Number,
    required: false,
    default: null,
  },
  isStage: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["emitReloadTour", "openItemDetailsOffCanvas", "moveItemToAnotherDay"]);

const { openModal, closeModal, modalToShow } = useModalUtils();
const concepts = useConcepts();
const itemTypes = computed(() => {
  return concepts.concepts.value?.["supplier.type"] ? mapConcepts(concepts.concepts.value["supplier.type"]) : [];
});

const dragOptions = computed(() => ({
  animation: 0,
  group: "tour-day-item",
  disabled: false,
  ghostClass: "ghost",
  preventOnFilter: false,
}));

const hotelDragOptions = computed(() => ({
  animation: 150,
  group: "hotel-list",
  disabled: props.updatingTour,
  ghostClass: "hotel-ghost",
  preventOnFilter: false,
}));
const showHotelDropdown = ref<boolean>(false);
const { t } = useI18n();
const toast = useToast();
const userStore = useUserStore();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const $toast = instance?.appContext.config.globalProperties.$toast;
const copyLuggageModalOpen = ref(false);

const groupedList = computed(() => {
  if (!props.list) return [];

  const hotels = props.list.filter((item: any) => item.type === "hotel");
  const others = props.list.filter((item: any) => item.type !== "hotel");

  let groupedHotels: any[] = [];

  if (hotels.length > 1) {
    const sortedHotels = hotels.sort((a: any, b: any) => {
      const priorityA = a.priorityOrder || a.position || 0;
      const priorityB = b.priorityOrder || b.position || 0;
      return priorityA - priorityB;
    });
    
    groupedHotels.push({
      id: sortedHotels.map((hotel) => hotel.id).join("-"),
      hotel: sortedHotels,
      disabled: sortedHotels.some((hotel) => hotel.disabled),
      type: "hotel",
      supplier: "Multiple Hotels",
    });
  } else if (hotels.length === 1) {
    groupedHotels = hotels;
  }

  return [...groupedHotels, ...others];
});

const toggleHotelList = (value: boolean) => {
  showHotelDropdown.value = value;
};

const onChanged = ({ moved, added }: any) => {
  if (moved) {
    const templateDay = props.tourMapped?.tourDays?.find((tourDay: any) => tourDay.id === moved.element.activityDayId);

    if (templateDay) {
      const itemReplaced = templateDay.items[moved.newIndex];
      moveItemPosition(moved.element, itemReplaced.position);
    }
  }

  if (added) {
    const dayItemsWithNewItem = props.list?.filter((item: any) => item.disabled || item.id !== added.element.id);

    if (dayItemsWithNewItem?.length) {
      const itemReplaced = dayItemsWithNewItem[added.newIndex];

      const data = {
        element: added.element,
        newPosition: itemReplaced ? (itemReplaced as any).position : (dayItemsWithNewItem.at(-1) as any)?.position + 1 || 1,
      };

      emit("moveItemToAnotherDay", data);
    }
  }
};

const onHotelChanged = ({ moved }: any) => {
  if (moved) {
    updateHotelPositions(moved.element, moved.newIndex);
  }
};

const updateHotelPositions = async (movedHotel: Record<string, any>, newIndex: number) => {
  try {
    const reqParam: Record<string, any> = {
      model_type: movedHotel.modelType,
      model_id: movedHotel.modelId,
      priority_order: newIndex + 1,
    };

    await api.activityService.updateDayItem(
      props.agency, 
      props.templateId, 
      (movedHotel as any).activityDayId || movedHotel.tourDayId, 
      movedHotel.id, 
      reqParam
    );

    toast.success(t("general.shared.savedChanges"), {
      position: "bottom-right" as any,
    });
    emit("emitReloadTour");
  } catch (e: any) {
    toast.error(e.response?.data?.message || t("tourBuilder.hotelReorderError"), {
      position: "bottom-right" as any,
    });
    emit("emitReloadTour");
  }
};

const moveItemPosition = async (item: Record<string, any>, newPosition: number) => {
  try {
    const reqParam: Record<string, any> = {
      model_type: item.modelType,
      model_id: item.modelId,
      position: newPosition,
      priority_order: newPosition,
    };

    await api.activityService.updateDayItem(props.agency, props.templateId, item.activityDayId, item.id, reqParam);

    toast.success(t("general.shared.savedChanges"), {
      position: "bottom-right" as any,
    });
    emit("emitReloadTour");
  } catch (e: any) {
    toast.error(e.response.data.message, {
      position: "bottom-right" as any,
    });
    emit("emitReloadTour");
  }
};

/** Match old builder: stage days measure distance from destination; rest days from origin. */
const resolveLocationId = () =>
  props.isStage ? props.destinationId : props.originId;

const setCurrentItem = (item: any) => {
  const data = {
    agency: props.agency,
    templateId: props.templateId,
    templateDayId: item.tourDayId ?? item.activityDayId,
    templateDayItemId: item.id,
    locationId: resolveLocationId(),
  };
  emit("openItemDetailsOffCanvas", data);
};

const openCopyLuggageModal = async (item: any) => {
  try {
   await userStore.setCurrentActivityDayItemV2({
      agency: props.agency,
      templateId: props.templateId,
      templateDayId: Number(item.tourDayId ?? item.activityDayId ?? props.templateDayId),
      templateDayItemId: Number(item.id),
      locationId: resolveLocationId(),
    });

    copyLuggageModalOpen.value = true;
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || "Error");
  }
};

const searchItem = (type: any) => {
  return itemTypes.value.find((itemType) => itemType.key === type);
};

const resolveItemTypeKey = (item: any): string => {
  const rawType = String(item?.type || "").toLowerCase();
  if (rawType) return rawType;
  return String(item?.model?.type || "").toLowerCase();
};

const resolveItemIconKey = (item: any): string => {
  return (
    searchItem(String(item?.type || ""))?.icon ||
    searchItem(String(item?.model?.type || ""))?.icon ||
    "question"
  );
};

const isLuggageItem = (item: any): boolean => resolveItemTypeKey(item) === "transport";

const deleteItem = async (templateDayItem: Record<string, any>, name: null | string = null) => {
  if ($modal) {
    await $modal.delete({
      title: t("tourBuilder.deleteTourDayItemTitle"),
      text: t("tourBuilder.deleteTourDayItemText", {
        dayItem: name ?? (findConceptByKey("supplier.type", templateDayItem.type) as any)?.name,
      }),
      deleteButtonText: t("general.button.delete"),
      rightButtonClasses: "btn-tertiary",
      deleteButtonCallback: () => doDeleteItem(templateDayItem.id),
    });
  }
};

const doDeleteItem = async (templateDayItem: number) => {
  if (!$toast) return;
  const loadingToast = $toast.loading(t("general.button.deleting"), "toast-remove-template-day-item");

  try {
    await api.activityService.deleteActivityDayItem(
      props.agency,
      props.templateId,
      props.templateDayId,
      templateDayItem
    );

    emit("emitReloadTour");

    loadingToast.update("toast-remove-template-day-item", {
      content: t("tourBuilder.dayItemDeleted"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    console.error(e.response?.data);

    loadingToast.update("toast-remove-template-day-item", {
      content: t("tourBuilder.dayItemDeleteError"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });

    toast.error(e.response?.data?.message || t("tourBuilder.dayItemDeleteError"), {
      position: "bottom-right" as any,
    });
  }
};

const addHotel = async () => {
  if (!$toast) return;
  const loadingToast = $toast.loading(t("general.button.adding"), "toast-add-template-day-item");
  try {
    const response = await api.activityService.createDayItem(props.agency, props.templateId, props.templateDayId, {
      type: "hotel",
    });

    const data = {
      id: response.id,
      tourDayId: (response as any).activityDayId || response.tourDayId,
    };
    setCurrentItem(data);
    const groupNights = getTotalNights();
    if (groupNights && groupNights > 1) {
      try {
        await api.activityService.updateDayItem(
          props.agency,
          props.templateId,
          Number((response as any).activityDayId || response.tourDayId),
          Number(response.id),
          { nights: Math.min(groupNights, getMaxNights()) }
        );
      } catch (e: any) {
        console.error(e.response?.data);
      }
    }
    emit("emitReloadTour");

    loadingToast.update("toast-add-template-day-item", {
      content: t("tourBuilder.dayItemCreated"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    loadingToast.update("toast-add-template-day-item", {
      content: t("tourBuilder.dayITemCreateError"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });

    console.error(e.response?.data);
    toast.error(e.response?.data?.message, {
      position: "bottom-right" as any,
      timeout: 5000,
    });
  }
};

const getTotalNights = (): number => {
  if (!props.list) return 1;
  const hotels = props.list.filter((item: any) => item.type === "hotel" && !item.isAutoGenerated);
  if (hotels.length === 0) return 1;
  return Number(hotels[0].nights) || 1;
};

const getMinNights = (): number => {
  if (!props.list) return 1;
  const hotels = props.list.filter((item: any) => item.type === "hotel" && !item.isAutoGenerated);
  if (hotels.length === 0) return 1;
  return Math.min(...hotels.map((hotel: any) => hotel.nights || 1));
};

const getMaxNights = (): number => {
  if (!props.tourMapped?.tourDays) return 1;
  const tourDays: any[] = (props.tourMapped as any).tourDays;
  const totalDays = tourDays.length;
  const currentDay = tourDays.find((d: any) => Number(d.id) === Number(props.templateDayId));
  const startOrder = Number(currentDay?.order || 1);

  let nextHotelOrder: number | null = null;
  for (const day of tourDays) {
    const order = Number(day?.order || 1);
    if (order <= startOrder) continue;
    const hasBlockingHotel = (day.items || []).some((it: any) => it.type === "hotel" && !it.isAutoGenerated && !it.disabled);
    if (hasBlockingHotel) {
      nextHotelOrder = order;
      break;
    }
  }

  if (nextHotelOrder !== null) {
    return Math.max(1, Number(nextHotelOrder) - Number(startOrder));
  }

  return Math.max(1, totalDays - Number(startOrder));
};

const increaseNights = async () => {
  if (!$toast) return;
  const maxNights = getMaxNights();
  const currentNights = getTotalNights();
  
  if (currentNights >= maxNights) {
    toast.warning(t("tourBuilder.maxNightsReached", { maxNights }), {
      position: "bottom-right" as any,
    });
    return;
  }
  
  const loadingToast = $toast.loading(t("general.button.updating"), "toast-update-nights");
  
  try {
    const hotels = props.list?.filter((item: any) => item.type === "hotel" && !item.isAutoGenerated) || [];
    
    for (const hotel of hotels) {
      const hotelNights = Number(hotel.nights) || 1;
      const newNights = Math.min(hotelNights + 1, maxNights);
      await api.activityService.updateDayItem(
        props.agency,
        props.templateId,
        Number(hotel.activityDayId),
        Number(hotel.id),
        { nights: newNights }
      );
    }
    
    emit("emitReloadTour");
    
    loadingToast.update("toast-update-nights", {
      content: t("general.shared.savedChanges"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    console.error(e.response?.data);
    
    loadingToast.update("toast-update-nights", {
      content: t("general.shared.error"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });
    
    toast.error(e.response?.data?.message || t("general.shared.error"), {
      position: "bottom-right" as any,
    });
  }
};

const decreaseNights = async () => {
  if (!$toast) return;
  const loadingToast = $toast.loading(t("general.button.updating"), "toast-update-nights");
  
  try {
    const hotels = props.list?.filter((item: any) => item.type === "hotel" && !item.isAutoGenerated) || [];
    
    for (const hotel of hotels) {
      const currentNights = Number(hotel.nights) || 1;
      const newNights = Math.max(1, currentNights - 1);
      await api.activityService.updateDayItem(
        props.agency,
        props.templateId,
        Number(hotel.activityDayId),
        Number(hotel.id),
        { nights: newNights }
      );
    }
    
    emit("emitReloadTour");
    
    loadingToast.update("toast-update-nights", {
      content: t("general.shared.savedChanges"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    console.error(e.response?.data);
    
    loadingToast.update("toast-update-nights", {
      content: t("general.shared.error"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });
    
    toast.error(e.response?.data?.message || t("general.shared.error"), {
      position: "bottom-right" as any,
    });
  }
};

const deleteAllHotels = async () => {
  if ($modal) {
    await $modal.delete({
      title: t("tourBuilder.deleteAllHotels"),
      text: t("tourBuilder.deleteAllHotelsConfirmation"),
      deleteButtonText: t("general.button.delete"),
      rightButtonClasses: "btn-tertiary",
      deleteButtonCallback: () => doDeleteAllHotels(),
    });
  }
};

const doDeleteAllHotels = async () => {
  if (!$toast) return;
  const loadingToast = $toast.loading(t("general.button.deleting"), "toast-delete-all-hotels");

  try {
    const hotels = props.list?.filter((item: any) => item.type === "hotel" && !item.isAutoGenerated) || [];
    
    for (const hotel of hotels) {
      await api.activityService.deleteActivityDayItem(
        props.agency,
        props.templateId,
        props.templateDayId,
        Number(hotel.id)
      );
    }

    emit("emitReloadTour");

    loadingToast.update("toast-delete-all-hotels", {
      content: t("tourBuilder.dayItemDeleted"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    console.error(e.response?.data);

    loadingToast.update("toast-delete-all-hotels", {
      content: t("tourBuilder.dayItemDeleteError"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });

    toast.error(e.response?.data?.message || t("tourBuilder.dayItemDeleteError"), {
      position: "bottom-right" as any,
    });
  }
};

const emitReloadTour = () => {
  emit("emitReloadTour");
};
</script>

<style lang="scss" scoped>
.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}

.list-group {
  min-height: 20px;
}

.list-group-item {
  cursor: move;
}

.list-group-item i {
  cursor: pointer;
}

.supplier-name {
  width: 4.25rem;
  overflow: hidden;
}

.list-enter-from {
  opacity: 0;
  transform: scaleY(0.25);
}

.list-enter-active,
.list-leave-active {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-to {
  opacity: 0;
  transform: scaleY(0);
}

.list-move {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-active {
  position: relative;
}

.list-group-item {
  display: block;
  padding: 0;
  border-left: none;
  border-right: none;
  border-top: none;
  border-radius: 0;
  width: 100%;
}
.tour-day-item-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 100%;
  flex-wrap: wrap;
}

.item-container {
  margin-right: 1.5rem;
  margin-bottom: 1rem;
  transition: all ease 0.2s;

  &:not(.item-selected):not(.ghost-item):hover {
    transform: translateY(-5px);

    .tour-day-item {
      box-shadow: 0 10px 20px 2px rgba(107, 107, 109, .25);
    }
  }

  .tour-day-item {
    border: 1px solid #dee2e6;
    transition: all ease 0.2s;
    box-shadow: 0 5px 10px 0 rgba(107, 107, 109, .5);
    border-radius: 4px;
    width: 3.5rem;
    height: 3.5rem;
    background-color: #fff;
  }

  .tour-day-item-actions {
    visibility: hidden;
    margin-bottom: 2px;
  }

  &:hover:not(.ghost-item) .tour-day-item-actions {
    visibility: visible;
  }
}

.btn-add-plus {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  min-height: 20px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: 0 !important;
}

.item-selected:not(.ghost-item) {
  transform: translateY(-5px);
  z-index: 9999 !important; /* Force to top when selected */

  .tour-day-item-actions {
    visibility: visible !important;
  }

  .tour-day-item {
    box-shadow: 0 10px 20px 2px rgba(var(--uq-primary-rgb), .4);
    border-color: var(--uq-primary);
  }
}

.dropdown-menu {
  min-width: 250px;
  z-index: 10000 !important; /* Above even the item container */
}

.custom-scroll {
  max-height: 200px; 
  overflow-y: auto;
  overflow-x: hidden;
}

::-webkit-scrollbar {
  width: 8px;
  background-color: #d5d5d5;
  cursor: auto;
}

::-webkit-scrollbar-thumb {
  cursor: auto;
  background: #d7df23;
  border-radius: 10px;
}

.custom-tooltip {
  position: relative;
  display: inline-block;
}

.custom-tooltip-text {
  visibility: hidden;
  opacity: 0;
  background-color: #ee2a7b;
  color: #fff;
  text-align: center;
  border-radius: 4px;
  padding: 4px 8px;
  position: absolute;
  z-index: 99999999;
  top: 45%;
  right: 125%;
  transform: translateY(-50%);
  white-space: nowrap;
  transition: opacity 0.2s;
  font-size: 12px;
}

.right-arrow {
  position: absolute;
  top: 50%;
  right: -6px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-left: 6px solid #ee2a7b;
}

.custom-tooltip:hover .custom-tooltip-text {
  visibility: visible;
  opacity: 1;
}
.hotel-actions {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
}
.hotel-row:hover .hotel-actions {
  opacity: 1;
  pointer-events: auto;
}

.drag-handle {
  cursor: grab;
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
}

.drag-handle:hover {
  opacity: 1;
}

.hotel-row:hover .drag-handle {
  opacity: 1;
}

.hotel-ghost {
  opacity: 0.5;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
}

.hotel-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.service-icon {
  transition: color 0.2s ease;
}

.cursor-grab {
  cursor: grab;
}

.cursor-grab:active {
  cursor: grabbing;
}
</style>
